import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { DEFAULT_CONTAINER_COL_STYLE } from '../../../constants/scenarioBuilder.constant'
import {
    ActionMode,
    CalculationMethods,
    CurrencyForecastType,
    CurrencyRelativeType,
    HedgingParamType,
    SCENARIO_CURVE_TYPES,
    YieldCurveShockTypes,
    YieldCurveValuesType,
} from '../../../enums/scenario-builder.enum'
import {
    TFxShock,
    TYieldCurveShock,
} from '../../../interfaces/scenarioBuilder.interface'
// import { LoadingButton } from "../../Shared/loading-button"
import { LoadingCard } from '@/components/Base/LoadingCard/LoadingCard'
import { YieldCurveTypeOptions } from '@/components/Shared/yieldCurveTypeOptions'
import {
    createReportTemplate,
    getReportTemplateDetails,
    updateReportTemplate,
} from '@/services/scenarioBuilder.service'

import { useAppDispatch } from '@/hooks/use-app-dispatch'
import { useAppSelector } from '@/hooks/use-app-selector'
import {
    setActionMode,
    setHasNewModifiedValues,
    setSaveButtonDisabled,
    setSelectedReportTemplate,
    toggleIsReloadingReportTemplateGrid,
} from '@/states/scenarioBuilder.slice'
import { Add } from '@mui/icons-material'
import { toast } from 'react-toastify'

import {
    TReportTemplate,
    TReportTemplateDetails,
    TScenario,
} from '../../../interfaces/scenarioBuilder.interface'
import { RootState } from '../../../store'
import { getAvailableCurrencies } from './currency.utils'
import ScenarioAccordion from './ScenarioAccordion'

// Empty yield curve shock
const emptyYieldCurveShocks: TYieldCurveShock = {
  curve: 'USD',
  d1: 0,
  d2: 0,
  d3: 0,
  k0_2: 0,
  k0_5: 0,
  k1: 0,
  k2: 0,
  k3: 0,
  k4: 0,
  k5: 0,
  k7: 0,
  k10: 0,
  k15: 0,
  k20: 0,
  k25: 0,
  k30: 0,
  k40: 0,
}

type ReportTemplateDetailsProps = {
  reportTemplate: TReportTemplate
}

export const ScenarioTemplateDetails: React.FC<ReportTemplateDetailsProps> = ({
  reportTemplate,
}) => {
  // Redux state
  const { actionMode, isSaveButtonDisabled, userInfo } = useAppSelector(
    (state: RootState) => state.scenarioBuilder,
  )

  // variables
  const dispatch = useAppDispatch()
  const isViewOnly = actionMode === ActionMode.VIEW
  const isCreateMode = actionMode === ActionMode.CREATE
  const isUpdateMode = actionMode === ActionMode.UPDATE
  const isDeleteMode = actionMode === ActionMode.DELETE

  const generateReportTemplateDetails = (
    template: TReportTemplate,
  ): TReportTemplateDetails => {
    return {
      name: template.name,
      reportType: template.reportType,
      horizon: 1,
      calculationMethod: CalculationMethods.FULL_VALIDATION,
      yieldCurveType: YieldCurveValuesType.PAR,
      username: userInfo.name,
      hedgingParam: HedgingParamType.UNHEDGED,
      hedgedValue: 0,
      currencyForecast: CurrencyForecastType.RETURN,
      currencyRelative: CurrencyRelativeType.USD,

      // scenarios will start with at-least one scenario by default
      scenarios: [
        {
          name: `Scenario`,
          yieldCurveShockType: YieldCurveShockTypes.D_VALUES,
          yieldCurveShocks: [{ ...emptyYieldCurveShocks }],
          fxShocks: [],
        },
      ],
    } as unknown as TReportTemplateDetails
  }
  const [isSaveTemplate, setIsSaveTemplate] = useState(false)
  const [reportTemplateDetails, setReportTemplateDetails] =
    useState<TReportTemplateDetails>(
      generateReportTemplateDetails(reportTemplate),
    )

  // Store original subset names from GET request for reliable old value capture
  const originalSubsetNamesRef = useRef<Map<string, string>>(new Map())
  const uniqueOriginalNamesRef = useRef<string[]>([])

  // Helper function to populate original subset names from GET data
  const populateOriginalSubsetNames = () => {
    // Prevent duplicate population
    if (originalSubsetNamesRef.current.size > 0) {
      return
    }
    
    const map = new Map<string, string>()
    const uniqueNames = new Set<string>()
    
    reportTemplateDetails?.scenarios?.forEach((scenario, scenarioIndex) => {
      // Store original names for each shock type
      scenario.equityShocks?.forEach((shock, shockIndex) => {
        const key = `${scenarioIndex}-${shockIndex}-equity`
        map.set(key, shock.subsetName)
        uniqueNames.add(shock.subsetName)
      })
      
      scenario.prepaymentShocks?.forEach((shock, shockIndex) => {
        const key = `${scenarioIndex}-${shockIndex}-prepayment`
        map.set(key, shock.subsetName)
        uniqueNames.add(shock.subsetName)
      })
      
      scenario.volatilityShocks?.forEach((shock, shockIndex) => {
        const key = `${scenarioIndex}-${shockIndex}-volatility`
        map.set(key, shock.subsetName)
        uniqueNames.add(shock.subsetName)
      })
      
      scenario.spreadsShocks?.forEach((shock, shockIndex) => {
        const key = `${scenarioIndex}-${shockIndex}-spreads`
        map.set(key, shock.subsetName)
        uniqueNames.add(shock.subsetName)
      })
    })
    
    // Store both the detailed map and unique names array
    originalSubsetNamesRef.current = map
    uniqueOriginalNamesRef.current = Array.from(uniqueNames)
  }

  const [lastSavedValues, setLastSavedValues] =
    useState<TReportTemplateDetails | null>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [subsetValidationError, setSubsetValidationError] = useState<string>('')

  // Populate original subset names when template data changes
  useEffect(() => {
    if (reportTemplateDetails) {
      populateOriginalSubsetNames()
    }
  }, [reportTemplateDetails])


  // Add state to track disabled scenarios
  const [disabledScenarios, setDisabledScenarios] = useState<
    {
      index: number
      name: string
      shockType: string[]
    }[]
  >([])

  // handle both yield curve and fx shocks off
  const handleBothTypesOff = (
    scenarioIndex: number,
    isOff: boolean,
    scenarioShockType: string[],
  ) => {
    dispatch(setSaveButtonDisabled(false))
    setDisabledScenarios((prev) => prev.filter((s) => s.index !== scenarioIndex))
  }

  const getTooltipMessage = () => {
    // Check for subset validation error first
    if (subsetValidationError) {
      return `Cannot save template. ${subsetValidationError}`
    }

    // Check for duplicate scenario names
    if (hasAnyDuplicateScenarios) {
      const duplicateNames = reportTemplateDetails.scenarios
        .filter((scenario, index) => hasDuplicateScenarioNames[index])
        .map(scenario => scenario.name)
        .filter((name, index, arr) => arr.indexOf(name) === index) // Remove duplicates from the list
      return `Cannot save template. Duplicate scenario names found: ${duplicateNames.join(', ')}. Please ensure all scenario names are unique.`
    }

    // Check for duplicate subset names
    if (hasAnyDuplicateSubsetNames) {
      const allSubsetNames: string[] = []
      reportTemplateDetails.scenarios.forEach((scenario) => {
        scenario.spreadsShocks?.forEach((shock) => {
          if (shock.subsetName && shock.subsetName.trim() !== '') {
            allSubsetNames.push(shock.subsetName.trim())
          }
        })
        scenario.equityShocks?.forEach((shock) => {
          if (shock.subsetName && shock.subsetName.trim() !== '') {
            allSubsetNames.push(shock.subsetName.trim())
          }
        })
        scenario.prepaymentShocks?.forEach((shock) => {
          if (shock.subsetName && shock.subsetName.trim() !== '') {
            allSubsetNames.push(shock.subsetName.trim())
          }
        })
        scenario.volatilityShocks?.forEach((shock) => {
          if (shock.subsetName && shock.subsetName.trim() !== '') {
            allSubsetNames.push(shock.subsetName.trim())
          }
        })
      })
      
      const duplicateSubsetNames = allSubsetNames
        .filter((name, index) => allSubsetNames.indexOf(name) !== index)
        .filter((name, index, arr) => arr.indexOf(name) === index) // Remove duplicates from the list
      return `Cannot save template. Duplicate subset names found: ${duplicateSubsetNames.join(', ')}. Please ensure all subset names are unique.`
    }

    // Check for disabled scenarios
    const disableBothScenarios = disabledScenarios.filter(
      (scenario) => scenario.shockType.length === 2,
    )

    if (disableBothScenarios.length > 0) {
      const scenarioNames = disableBothScenarios
        .map((scenario) => scenario.name)
        .join(', ')
      return `Cannot save template. The following scenarios have both Yield Curve and FX disabled: ${scenarioNames}. Please enable at least one shock type for each scenario.`
    }

    // Check for general save button disabled state
    if (isSaveButtonDisabled) {
      return 'Save is currently disabled. Please check for validation errors.'
    }

    return ''
  }

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        // Chrome requires returnValue to be set
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasUnsavedChanges])

  // Ensure save button stays enabled by resetting Redux state
  useEffect(() => {
    dispatch(setSaveButtonDisabled(false))
  }, [dispatch])

  // Reset save button disabled state when template details change
  useEffect(() => {
    dispatch(setSaveButtonDisabled(false))
  }, [reportTemplateDetails, dispatch])



  const addNewScenario = () => {
    // Ensure save button stays enabled
    dispatch(setSaveButtonDisabled(false))
    
    // A separate empty object to be added and avoiding value modification issue
    const emptyYieldCurveShocks: TYieldCurveShock = {
      curve: 'USD',
      d1: 0,
      d2: 0,
      d3: 0,
      k0_2: 0,
      k0_5: 0,
      k1: 0,
      k2: 0,
      k3: 0,
      k4: 0,
      k5: 0,
      k7: 0,
      k10: 0,
      k15: 0,
      k20: 0,
      k25: 0,
      k30: 0,
      k40: 0,
    }

    if (reportTemplateDetails) {
      // Generate indexed scenario name
      const scenarioIndex = reportTemplateDetails.scenarios.length + 1
      const scenarioName = `Scenario ${scenarioIndex}`
      
      // Get existing criteria from first scenario (if any) to copy to new scenario
      const firstScenario = reportTemplateDetails.scenarios[0]
      const copiedSpreadsShocks = firstScenario?.spreadsShocks ? 
        JSON.parse(JSON.stringify(firstScenario.spreadsShocks)) : []
      const copiedEquityShocks = firstScenario?.equityShocks ? 
        JSON.parse(JSON.stringify(firstScenario.equityShocks)) : []
      const copiedPrepaymentShocks = firstScenario?.prepaymentShocks ? 
        JSON.parse(JSON.stringify(firstScenario.prepaymentShocks)) : []
      const copiedVolatilityShocks = firstScenario?.volatilityShocks ? 
        JSON.parse(JSON.stringify(firstScenario.volatilityShocks)) : []

      const newScenario: TScenario = {
        name: scenarioName,
        yieldCurveShockType: YieldCurveShockTypes.D_VALUES,
        // Create a new copy of the empty object, shallow copy will work as object is not nested.
        yieldCurveShocks: [{ ...emptyYieldCurveShocks }],
        fxShocks: [],
        spreadsShocks: copiedSpreadsShocks,
        equityShocks: copiedEquityShocks,
        prepaymentShocks: copiedPrepaymentShocks,
        volatilityShocks: copiedVolatilityShocks,
      }

      setReportTemplateDetails((prevDetails: TReportTemplateDetails) => {
        return {
          ...prevDetails,
          scenarios: prevDetails.scenarios.concat(newScenario),
        }
      })
      dispatch(setHasNewModifiedValues(true))
    }
  }

  // Transform API response to UI format
  const transformApiResponseToUIFormat = (apiData: any) => {
    if (!apiData.scenarios) return apiData

    const transformedScenarios = apiData.scenarios.map((scenario: any) => {
      // If already in UI format, return as is
      if (scenario.spreadsShocks || scenario.equityShocks || scenario.prepaymentShocks || scenario.volatilityShocks) {
        return scenario
      }

      // Transform from API format (subsetShifts) to UI format
      const spreadsShocks: any[] = []
      const equityShocks: any[] = []
      const prepaymentShocks: any[] = []
      const volatilityShocks: any[] = []

      if (scenario.subsetShifts) {
        scenario.subsetShifts.forEach((shift: any, index: number) => {
          // Get screening titles for each shock type
          const spreadTitle = apiData.spreadScreeningTitles?.find((title: any) => title.criteriaName === shift.subsetName)
          const equityTitle = apiData.equityScreeningTitles?.find((title: any) => title.criteriaName === shift.subsetName)
          const prepaymentTitle = apiData.prepaymentScreeningTitles?.find((title: any) => title.criteriaName === shift.subsetName)
          const volatilityTitle = apiData.volatilityScreeningTitles?.find((title: any) => title.criteriaName === shift.subsetName)

          // Only create spreads shock if there's actual screening title data
          if (spreadTitle?.criteriaName) {
            spreadsShocks.push({
              subsetName: spreadTitle.criteriaName,
              screenCriteria: spreadTitle.screenCriteriasTitle || spreadTitle.criteriaName,
              factorShift: shift.factorShift || 1,
              offsetShift: shift.offsetShift || 0
            })
          }

          // Only create equity shock if there's actual screening title data
          if (equityTitle?.criteriaName) {
            equityShocks.push({
              subsetName: equityTitle.criteriaName,
              screenCriteria: equityTitle.screenCriteriasTitle || equityTitle.criteriaName,
              eqtyMktShock: shift.eqtyMktShock === -9.999999933815813E36 ? 0 : shift.eqtyMktShock || 0,
              subsetReturnOverride: shift.subsetReturnOverride === -9.999999933815813E36 ? 0 : shift.subsetReturnOverride || 0
            })
          }

          // Only create prepayment shock if there's actual screening title data
          if (prepaymentTitle?.criteriaName) {
            prepaymentShocks.push({
              subsetName: prepaymentTitle.criteriaName,
              screenCriteria: prepaymentTitle.screenCriteriasTitle || prepaymentTitle.criteriaName,
              prepayVal: shift.prepayVal || 1,
              prepayType: shift.prepayType || 0
            })
          }

          // Only create volatility shock if there's actual screening title data
          if (volatilityTitle?.criteriaName) {
            volatilityShocks.push({
              subsetName: volatilityTitle.criteriaName,
              screenCriteria: volatilityTitle.screenCriteriasTitle || volatilityTitle.criteriaName,
              defaultMult: shift.defaultMult || 1,
              recoveryMult: shift.recoveryMult || 1,
              shortVolMult: shift.shortVolMult || 1,
              longVolMult: shift.longVolMult || 1
            })
          }
        })
      }

      return {
        ...scenario,
        spreadsShocks,
        equityShocks,
        prepaymentShocks,
        volatilityShocks
      }
    })

    return {
      ...apiData,
      scenarios: transformedScenarios
    }
  }

  useEffect(() => {
    if (actionMode === ActionMode.CREATE) {
      setReportTemplateDetails(generateReportTemplateDetails(reportTemplate))
    } else if (
      (actionMode === ActionMode.VIEW || actionMode === ActionMode.UPDATE) &&
      reportTemplate.id
    ) {
      const fetchReportTemplateDetails = async () => {
        setIsLoading(true)
        try {
          const response = await getReportTemplateDetails(
            reportTemplate.reportType,
            reportTemplate.id,
          )
          
          // Store original screening data globally for validation
          const originalData = response.data[0]
          
          if (originalData.screening) {
            (window as any).__TEMPLATE_SCREENING_DATA__ = originalData.screening
          }
          
          // Transform the API response to UI format
          const transformedData = transformApiResponseToUIFormat(originalData)
          
          // Preserve original screening data in transformed data
          transformedData.screening = originalData.screening
          
          setReportTemplateDetails(transformedData)
          setLastSavedValues(JSON.parse(JSON.stringify(transformedData)))
          
          // Store screening data globally for tab-level validation
          const screeningData = originalData.screenings || 
                               originalData.screening || 
                               originalData.criterias || 
                               originalData.subsetScreening || 
                               []
          
          if (screeningData.length > 0) {
            (window as any).__TEMPLATE_SCREENING_DATA__ = screeningData
          }
        } catch (error) {
          console.error('Error fetching scenario details:', error)
        } finally {
          dispatch(setHasNewModifiedValues(false))
          setIsLoading(false)
        }
      }
      fetchReportTemplateDetails()
    }
  }, [actionMode, reportTemplate])

  // No longer needed - we capture state on tab click instead

  // No need to show details table component, if we are just deleting from templates.
  if (isDeleteMode) {
    return null
  }

  // Function to transform payload to backend expected format
  const transformToBackendPayload = (currentDetails: any) => {
    const payloadToSend = JSON.parse(JSON.stringify(currentDetails))

    // Transform scenarios to backend format
    payloadToSend.scenarios = payloadToSend.scenarios.map((scenario: TScenario, index: number) => {
      const disabledScenario = disabledScenarios.find((s) => s.index === index)

      // Handle disabled scenarios
      if (disabledScenario && lastSavedValues.scenarios[index]) {
        const originalScenario = lastSavedValues.scenarios[index]

        // For disabled Yield Curve, use values from lastSavedValues
        if (disabledScenario.shockType.includes(SCENARIO_CURVE_TYPES.YIELD_CURVE)) {
          scenario.yieldCurveShocks = originalScenario.yieldCurveShocks
          scenario.yieldCurveShockType = originalScenario.yieldCurveShockType
        }

        // For disabled FX, use values from lastSavedValues
        if (disabledScenario.shockType.includes(SCENARIO_CURVE_TYPES.FX)) {
          scenario.fxShocks = originalScenario.fxShocks
        }

        // For disabled shock types, revert to saved values
        if (disabledScenario.shockType.includes(SCENARIO_CURVE_TYPES.SPREADS)) {
          scenario.spreadsShocks = originalScenario.spreadsShocks
        }
        if (disabledScenario.shockType.includes(SCENARIO_CURVE_TYPES.EQUITY)) {
          scenario.equityShocks = originalScenario.equityShocks
        }
        if (disabledScenario.shockType.includes(SCENARIO_CURVE_TYPES.PREPAYMENT)) {
          scenario.prepaymentShocks = originalScenario.prepaymentShocks
        }
        if (disabledScenario.shockType.includes(SCENARIO_CURVE_TYPES.VOLATILITY)) {
          scenario.volatilityShocks = originalScenario.volatilityShocks
        }
      }

      // Transform to backend format: combine all shock types into subsetShifts
      const subsetShifts: any[] = []
      const scenarioScreenings: any[] = []

      if (scenario.spreadsShocks && scenario.spreadsShocks.length > 0) {
        scenario.spreadsShocks.forEach((shock: any) => {
          const subsetShift = {
            subsetName: shock.subsetName,
            factorShift: shock.factorShift || 1,
            offsetShift: shock.offsetShift || 0,
            prepayVal: 1,
            prepayType: 0,
            defaultMult: 1,
            recoveryMult: 1,
            shortVolMult: 1,
            longVolMult: 1,
            eqtyMktShock: -9.999999933815813e+36,
            subsetReturnOverride: -9.999999933815813e+36
          }
          subsetShifts.push(subsetShift)

          // Create screenCriterias for new subsets using the detailed criteria data
          if (shock.criteriaData) {
            scenarioScreenings.push({
              criteriaName: shock.subsetName,
              screenCriterias: shock.criteriaData.screenCriterias || [shock.criteriaData]
            })
          }
        })
      }

      // Process equity shocks
      if (scenario.equityShocks && scenario.equityShocks.length > 0) {
        scenario.equityShocks.forEach((shock: any) => {
          const existingSubset = subsetShifts.find(s => s.subsetName === shock.subsetName)
          if (existingSubset) {
            existingSubset.eqtyMktShock = shock.eqtyMktShock || -9.999999933815813e+36
            existingSubset.subsetReturnOverride = shock.subsetReturnOverride || -9.999999933815813e+36
          } else {
            subsetShifts.push({
              subsetName: shock.subsetName,
              factorShift: 1,
              offsetShift: 0,
              prepayVal: 1,
              prepayType: 0,
              defaultMult: 1,
              recoveryMult: 1,
              shortVolMult: 1,
              longVolMult: 1,
              eqtyMktShock: shock.eqtyMktShock || -9.999999933815813e+36,
              subsetReturnOverride: shock.subsetReturnOverride || -9.999999933815813e+36
            })
          }

          // Create screenCriterias for new equity subsets using the detailed criteria data
          if (shock.criteriaData) {
            scenarioScreenings.push({
              criteriaName: shock.subsetName,
              screenCriterias: shock.criteriaData.screenCriterias || [shock.criteriaData]
            })
          }
        })
      }

      // Process prepayment shocks
      if (scenario.prepaymentShocks && scenario.prepaymentShocks.length > 0) {
        scenario.prepaymentShocks.forEach((shock: any) => {
          const existingSubset = subsetShifts.find(s => s.subsetName === shock.subsetName)
          if (existingSubset) {
            existingSubset.prepayVal = shock.prepayVal || 1
            existingSubset.prepayType = shock.prepayType || 0
          } else {
            subsetShifts.push({
              subsetName: shock.subsetName,
              factorShift: 1,
              offsetShift: 0,
              prepayVal: shock.prepayVal || 1,
              prepayType: shock.prepayType || 0,
              defaultMult: 1,
              recoveryMult: 1,
              shortVolMult: 1,
              longVolMult: 1,
              eqtyMktShock: -9.999999933815813e+36,
              subsetReturnOverride: -9.999999933815813e+36
            })
          }

          // Create screenCriterias for new prepayment subsets using the detailed criteria data
          if (shock.criteriaData) {
            scenarioScreenings.push({
              criteriaName: shock.subsetName,
              screenCriterias: shock.criteriaData.screenCriterias || [shock.criteriaData]
            })
          }
        })
      }

      // Process volatility shocks
      if (scenario.volatilityShocks && scenario.volatilityShocks.length > 0) {
        scenario.volatilityShocks.forEach((shock: any) => {
          const existingSubset = subsetShifts.find(s => s.subsetName === shock.subsetName)
          if (existingSubset) {
            existingSubset.defaultMult = shock.defaultMult || 1
            existingSubset.recoveryMult = shock.recoveryMult || 1
            existingSubset.shortVolMult = shock.shortVolMult || 1
            existingSubset.longVolMult = shock.longVolMult || 1
          } else {
            subsetShifts.push({
              subsetName: shock.subsetName,
              factorShift: 1,
              offsetShift: 0,
              prepayVal: 1,
              prepayType: 0,
              defaultMult: shock.defaultMult || 1,
              recoveryMult: shock.recoveryMult || 1,
              shortVolMult: shock.shortVolMult || 1,
              longVolMult: shock.longVolMult || 1,
              eqtyMktShock: -9.999999933815813e+36,
              subsetReturnOverride: -9.999999933815813e+36
            })
          }

          // Create screenCriterias for new volatility subsets using the detailed criteria data
          if (shock.criteriaData) {
            scenarioScreenings.push({
              criteriaName: shock.subsetName,
              screenCriterias: shock.criteriaData.screenCriterias || [shock.criteriaData]
            })
          }
        })
      }

      // Return transformed scenario (don't include screenings here, they go to root level)
      return {
        name: scenario.name,
        shortName: scenario.shortName || "",
        yieldCurveShockType: scenario.yieldCurveShockType,
        yieldCurveShocks: scenario.yieldCurveShocks,
        fxShocks: scenario.fxShocks,
        subsetShifts: subsetShifts,
        _tempScreenings: scenarioScreenings // Temporary field to collect screenings
      }
    })

    // Collect all screenings from scenarios (avoid duplicates)
    const allScreenings: any[] = []
    const addedCriteriaNames = new Set<string>()
    
    // Initialize shock-type-specific screening titles
    const spreadScreeningTitles: any[] = []
    const equityScreeningTitles: any[] = []
    const prepaymentScreeningTitles: any[] = []
    const volatilityScreeningTitles: any[] = []
    
    // Collect all current criteria names from all shock types
    const currentCriteriaNames = new Set<string>()
    currentDetails.scenarios.forEach((scenario: any) => {
      if (scenario.spreadsShocks) {
        scenario.spreadsShocks.forEach((shock: any) => currentCriteriaNames.add(shock.subsetName))
      }
      if (scenario.equityShocks) {
        scenario.equityShocks.forEach((shock: any) => currentCriteriaNames.add(shock.subsetName))
      }
      if (scenario.prepaymentShocks) {
        scenario.prepaymentShocks.forEach((shock: any) => currentCriteriaNames.add(shock.subsetName))
      }
      if (scenario.volatilityShocks) {
        scenario.volatilityShocks.forEach((shock: any) => currentCriteriaNames.add(shock.subsetName))
      }
    })
    
    // Use latest screenings from current state (includes real-time updates)
    if (reportTemplateDetails?.screenings) {
      reportTemplateDetails.screenings.forEach((existingScreening: any) => {
        allScreenings.push(existingScreening)
        addedCriteriaNames.add(existingScreening.criteriaName)
      })
    }
    
    // Then add new screenings from current scenarios and categorize by shock type
    payloadToSend.scenarios.forEach((scenario: any) => {
      if (scenario._tempScreenings) {
        scenario._tempScreenings.forEach((screening: any) => {
          if (!addedCriteriaNames.has(screening.criteriaName)) {
            allScreenings.push(screening)
            addedCriteriaNames.add(screening.criteriaName)
          }
        })
        delete scenario._tempScreenings
      }
    })

    const screeningSequence = allScreenings.map(s => s.criteriaName)
    
    payloadToSend.scenarios.forEach((scenario: any) => {
      // Reorder subsetShifts to match screening sequence
      const reorderedSubsetShifts: any[] = []
      
      screeningSequence.forEach((subsetName: string) => {
        let existingShift = scenario.subsetShifts?.find((shift: any) => shift.subsetName === subsetName)
        
        if (!existingShift) {
          existingShift = {
            subsetName: subsetName,
            factorShift: 1,
            offsetShift: 0,
            prepayVal: 1,
            prepayType: 0,
            defaultMult: 1,
            recoveryMult: 1,
            shortVolMult: 1,
            longVolMult: 1,
            eqtyMktShock: -9.999999933815813e+36,
            subsetReturnOverride: -9.999999933815813e+36
          }

          if (scenario.spreadsShocks) {
            const spreadsShock = scenario.spreadsShocks.find((s: any) => s.subsetName === subsetName)
            if (spreadsShock) {
              existingShift.factorShift = spreadsShock.factorShift || 1
              existingShift.offsetShift = spreadsShock.offsetShift || 0
            }
          }
          if (scenario.equityShocks) {
            const equityShock = scenario.equityShocks.find((s: any) => s.subsetName === subsetName)
            if (equityShock) {
              existingShift.eqtyMktShock = equityShock.eqtyMktShock || -9.999999933815813e+36
              existingShift.subsetReturnOverride = equityShock.subsetReturnOverride || -9.999999933815813e+36
            }
          }
          if (scenario.prepaymentShocks) {
            const prepaymentShock = scenario.prepaymentShocks.find((s: any) => s.subsetName === subsetName)
            if (prepaymentShock) {
              existingShift.prepayVal = prepaymentShock.prepayVal || 1
              existingShift.prepayType = prepaymentShock.prepayType || 0
            }
          }
          if (scenario.volatilityShocks) {
            const volatilityShock = scenario.volatilityShocks.find((s: any) => s.subsetName === subsetName)
            if (volatilityShock) {
              existingShift.defaultMult = volatilityShock.defaultMult || 1
              existingShift.recoveryMult = volatilityShock.recoveryMult || 1
              existingShift.shortVolMult = volatilityShock.shortVolMult || 1
              existingShift.longVolMult = volatilityShock.longVolMult || 1
            }
          }
        }
        
        reorderedSubsetShifts.push(existingShift)
      })
      
      scenario.subsetShifts = reorderedSubsetShifts
    })

    currentDetails.scenarios.forEach((scenario: any) => {
      if (scenario.spreadsShocks) {
        scenario.spreadsShocks.forEach((shock: any) => {
          if (shock.screenCriteria && 
              currentCriteriaNames.has(shock.subsetName) && 
              !spreadScreeningTitles.find(s => s.criteriaName === shock.subsetName)) {
            spreadScreeningTitles.push({
              criteriaName: shock.subsetName,
              screenCriteriasTitle: shock.screenCriteria
            })
          }
        })
      }

      // Process equity shocks  
      if (scenario.equityShocks) {
        scenario.equityShocks.forEach((shock: any) => {
          if (shock.screenCriteria && 
              currentCriteriaNames.has(shock.subsetName) && 
              !equityScreeningTitles.find(s => s.criteriaName === shock.subsetName)) {
            equityScreeningTitles.push({
              criteriaName: shock.subsetName,
              screenCriteriasTitle: shock.screenCriteria
            })
          }
        })
      }

      // Process prepayment shocks
      if (scenario.prepaymentShocks) {
        scenario.prepaymentShocks.forEach((shock: any) => {
          if (shock.screenCriteria && 
              currentCriteriaNames.has(shock.subsetName) && 
              !prepaymentScreeningTitles.find(s => s.criteriaName === shock.subsetName)) {
            prepaymentScreeningTitles.push({
              criteriaName: shock.subsetName,
              screenCriteriasTitle: shock.screenCriteria
            })
          }
        })
      }

      // Process volatility shocks
      if (scenario.volatilityShocks) {
        scenario.volatilityShocks.forEach((shock: any) => {
          if (shock.screenCriteria && 
              currentCriteriaNames.has(shock.subsetName) && 
              !volatilityScreeningTitles.find(s => s.criteriaName === shock.subsetName)) {
            const titleToAdd = {
              criteriaName: shock.subsetName,
              screenCriteriasTitle: shock.screenCriteria
            }
            volatilityScreeningTitles.push(titleToAdd)
          }
        })
      }
    })

    // Add all screening arrays to payload
    payloadToSend.screenings = allScreenings

    // Don't preserve any of the extra subset fields - they shouldn't be in PUT payload
    delete (payloadToSend as any).numSubsets
    delete (payloadToSend as any).subsetNames  
    delete (payloadToSend as any).subsetStrings
    delete (payloadToSend as any).subsetTypes
    delete (payloadToSend as any).subsetDBStrings
    delete (payloadToSend as any).spreadScreeningTitles
    delete (payloadToSend as any).equityScreeningTitles
    delete (payloadToSend as any).prepaymentScreeningTitles
    delete (payloadToSend as any).volatilityScreeningTitles

    return payloadToSend
  }
  const handleCreateOrUpdate = async (): Promise<void> => {
    setIsLoading(true)
    try {
      const latestScenarioPayload = transformToBackendPayload(
        reportTemplateDetails,
      )
      

      // Debug: Log PUT payload to check subset name updates

      let response = isCreateMode
        ? await createReportTemplate(latestScenarioPayload)
        : await updateReportTemplate(reportTemplate.id, latestScenarioPayload)

      if (response.data[0].success) {
        // After successful save, fetch the latest data
        const updatedResponse = await getReportTemplateDetails(
          reportTemplate.reportType,
          response.data[0].id || reportTemplate.id,
        )

        // Update states with fresh data from server - transform API response to UI format
        const transformedUpdatedData = transformApiResponseToUIFormat(updatedResponse.data[0])
        setReportTemplateDetails(transformedUpdatedData)
        setLastSavedValues(JSON.parse(JSON.stringify(transformedUpdatedData)))

        // Update Redux state
        dispatch(setSelectedReportTemplate({
          ...updatedResponse.data[0],
          id: response.data[0].id || reportTemplate.id,
        }))

        if (isCreateMode) {
          dispatch(setActionMode(ActionMode.UPDATE))
        }

        toast.success(
          `Template "${latestScenarioPayload.name}" ${isCreateMode ? 'created' : 'updated'} successfully`,
        )

        dispatch(toggleIsReloadingReportTemplateGrid())
        setHasUnsavedChanges(false)
        dispatch(setHasNewModifiedValues(false))
      } else {
        toast.error(
          `Failed to ${isCreateMode ? 'create' : 'update'} report template. Please try again.`,
        )
      }
    } catch (error: any) {
      console.error('Error saving scenario details:', error)
      toast.error(
        `Failed to ${isCreateMode ? 'create' : 'update'} report template details. Please try again.`,
      )
    } finally {
      setIsLoading(false)
    }
  }

  // Check if all specified shock values are zero for a yield curve shock
  const hasZeroRowsInYieldCurveShock = (
    shocks: TYieldCurveShock[],
    yieldCurveType: YieldCurveShockTypes,
  ) => {
    // Define which keys to check based on shock type
    const keysToCheck =
      yieldCurveType === YieldCurveShockTypes.D_VALUES
        ? ['d1', 'd2', 'd3']
        : [
            'k0_2',
            'k0_5',
            'k1',
            'k2',
            'k3',
            'k5',
            'k7',
            'k10',
            'k15',
            'k20',
            'k25',
            'k30',
            'k40',
          ]

    // Return indices of shocks where all values are zero
    return shocks.reduce<number[]>((zeroIndices, shock, index) => {
      const hasAllZeroValues = keysToCheck.every(
        (key) => (shock[key as keyof TYieldCurveShock] || 0) === 0,
      )
      return hasAllZeroValues ? [...zeroIndices, index] : zeroIndices
    }, [])
  }

  // Check if screening array and shock data are empty and show warning
  const isPayloadContainsZeroRows = async () => {
    // Check if screenings array is empty or has no subsets
    const hasNoScreenings = !reportTemplateDetails.screenings || 
                           reportTemplateDetails.screenings.length === 0

    // Rule 1: Check if ALL scenarios have non-zero YC/FX values (valid even without subsets)
    const allScenariosHaveNonZeroYCFXData = reportTemplateDetails.scenarios.every(scenario => {
      // Check YC for non-zero values
      const hasNonZeroYC = scenario.yieldCurveShocks && 
                          scenario.yieldCurveShocks.length > 0 &&
                          scenario.yieldCurveShocks.some(shock => {
                            const ycKeys = scenario.yieldCurveShockType === YieldCurveShockTypes.D_VALUES
                              ? ['d1', 'd2', 'd3']
                              : ['k0_2', 'k0_5', 'k1', 'k2', 'k3', 'k5', 'k7', 'k10', 'k15', 'k20', 'k25', 'k30', 'k40']
                            return ycKeys.some(key => {
                              const value = shock[key as keyof typeof shock]
                              return value !== 0 && value !== '' && value !== null && value !== undefined
                            })
                          })

      // Check FX for non-zero values
      const hasNonZeroFX = scenario.fxShocks && 
                          scenario.fxShocks.length > 0 &&
                          scenario.fxShocks.some(shock => 
                            shock.currReturn !== 0 && shock.currReturn !== null && shock.currReturn !== undefined
                          )

      return hasNonZeroYC || hasNonZeroFX
    })

    // Rule 1: If ALL scenarios have non-zero YC/FX values, allow save regardless of S/E/P/V
    if (allScenariosHaveNonZeroYCFXData) {
      await handleCreateOrUpdate()
      return
    }

    // Check if ALL scenarios have S/E/P/V shock types with subsets
    const allScenariosHaveSubsets = reportTemplateDetails.scenarios.every(scenario => {
      const hasEquitySubsets = scenario.equityShocks && scenario.equityShocks.length > 0
      const hasSpreadsSubsets = scenario.spreadsShocks && scenario.spreadsShocks.length > 0  
      const hasPrepaymentSubsets = scenario.prepaymentShocks && scenario.prepaymentShocks.length > 0
      const hasVolatilitySubsets = scenario.volatilityShocks && scenario.volatilityShocks.length > 0
      
      return hasEquitySubsets || hasSpreadsSubsets || hasPrepaymentSubsets || hasVolatilitySubsets
    })

    // Check if ALL scenarios have YC/FX with any data (including zeros)
    const allScenariosHaveYCFXData = reportTemplateDetails.scenarios.every(scenario => {
      // Check if YC has data (including zeros)
      const hasYCData = scenario.yieldCurveShocks && 
                       scenario.yieldCurveShocks.length > 0 &&
                       scenario.yieldCurveShocks.some(shock => {
                         const ycKeys = scenario.yieldCurveShockType === YieldCurveShockTypes.D_VALUES
                           ? ['d1', 'd2', 'd3']
                           : ['k0_2', 'k0_5', 'k1', 'k2', 'k3', 'k5', 'k7', 'k10', 'k15', 'k20', 'k25', 'k30', 'k40']
                         return ycKeys.some(key => {
                           const value = shock[key as keyof typeof shock]
                           return value !== '' && value !== null && value !== undefined
                         })
                       })

      // Check if FX has data (including zeros)
      const hasFXData = scenario.fxShocks && 
                       scenario.fxShocks.length > 0 &&
                       scenario.fxShocks.some(shock => 
                         shock.currReturn !== null && shock.currReturn !== undefined
                       )

      return hasYCData || hasFXData
    })

    // Rules 2 & 3: If ALL scenarios have YC/FX data (including zeros) + ALL have subsets = valid
    if (allScenariosHaveYCFXData && allScenariosHaveSubsets) {
      await handleCreateOrUpdate()
      return
    }

    // No valid combination found
    const hasNoShockData = true

    // Show warning if screenings OR shock data are missing
    if (hasNoScreenings || hasNoShockData) {
      setIsSaveTemplate(true)
    } else {
      await handleCreateOrUpdate()
    }
  }

  // Handle confirmation of saving template with no subsets
  const handleConfirm = async () => {
    // Do not proceed with save if no screenings/subsets - just close dialog
    setIsSaveTemplate(false)
  }

  const handleClose = () => {
    setIsSaveTemplate(false)
  }

  const handleYieldCurveValueChange = (
    e: SelectChangeEvent<YieldCurveValuesType>,
  ) => {
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(false))
    setReportTemplateDetails(
      (prevState) =>
        ({
          ...prevState,
          yieldCurveType: e.target.value,
        }) as TReportTemplateDetails,
    )
  }

  const handleScenarioNameChange = (
    updateScenarioName: string,
    scenarioIndex: number,
  ) => {
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(false))
    const scenario = reportTemplateDetails.scenarios.find(
      (_, index) => index === scenarioIndex,
    )
    if (scenario) {
      scenario.name = updateScenarioName
    }
    setReportTemplateDetails({ ...reportTemplateDetails })
  }


  const handleScenarioTemplateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(false))
    setReportTemplateDetails({
      ...reportTemplateDetails,
      name: e.target.value,
    } as TReportTemplateDetails)
  }

  const handleHorizonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails({
      ...reportTemplateDetails,
      horizon: Math.max(0, +e.target.value),
    } as TReportTemplateDetails)
  }

  const handleCalculationMethodChange = (
    e: SelectChangeEvent<CalculationMethods>,
  ) => {
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    const newMode = e.target.value as CalculationMethods
    setReportTemplateDetails({
      ...reportTemplateDetails,
      calculationMethod: newMode,
    } as TReportTemplateDetails)
  }
  const handleHedgingParamChange = (e: SelectChangeEvent<HedgingParamType>) => {
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    const newParam = e.target.value as HedgingParamType

    // when user changes hedgingParam to unhedged, clear the previously selected hedgedValue to 0, so it reflects in UI
    setReportTemplateDetails({
      ...reportTemplateDetails,
      hedgingParam: newParam,
      // Set hedgedValue to 0 only when switching to UNHEDGED
      ...(newParam === HedgingParamType.UNHEDGED && { hedgedValue: 0 }),
    } as TReportTemplateDetails)
  }

  const handleCancelReportTemplateDetailsChange = () => {
    setHasUnsavedChanges(false)
    dispatch(setHasNewModifiedValues(false))
    dispatch(setActionMode(ActionMode.VIEW))
    dispatch(setSelectedReportTemplate(undefined))
  }

  // Handle updates for yield curve shock values
  const handleYieldCurveShockUpdate = (
    scenarioIndex: number,
    shockIndex: number,
    field: string,
    newValue: number,
  ) => {
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails) return prevDetails

      const updatedScenarios = [...prevDetails.scenarios]
      const updatedShocks = [...(updatedScenarios[scenarioIndex].yieldCurveShocks || [])]
      
      if (updatedShocks[shockIndex]) {
        updatedShocks[shockIndex] = {
          ...updatedShocks[shockIndex],
          [field]: newValue,
        }
      }

      updatedScenarios[scenarioIndex] = {
        ...updatedScenarios[scenarioIndex],
        yieldCurveShocks: updatedShocks,
      }

      return {
        ...prevDetails,
        scenarios: updatedScenarios,
      }
    })
  }


  // Handle updates for spreads shock values
  const handleSpreadsShockUpdate = (
    scenarioIndex: number,
    shockIndex: number,
    field: string,
    newValue: number | string,
  ) => {
    // Validate subset name if user is changing subsetName field
    if (field === 'subsetName' && typeof newValue === 'string') {
      const newSubsetName = newValue.trim()
      // Get current subset names excluding the one being edited
      const currentSubsetNames = getCurrentSubsetNames(scenarioIndex, shockIndex, 'spreads')
      if (currentSubsetNames.includes(newSubsetName)) {
        setSubsetValidationError(`Subset name "${newSubsetName}" already exists. Please choose a different name.`)
        return 
      }
      setSubsetValidationError('') 
    }

    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails) return prevDetails

      const updatedScenarios = [...prevDetails.scenarios]
      const updatedShocks = [...(updatedScenarios[scenarioIndex].spreadsShocks || [])]
      let updatedScreenings = prevDetails.screenings
      
      
      if (updatedShocks[shockIndex]) {
        // @ts-ignore - Dynamic field access needed for real-time sync
        const oldValue = updatedShocks[shockIndex][field]
        updatedShocks[shockIndex] = {
          ...updatedShocks[shockIndex],
          [field]: newValue,
        }
        
        // Real-time sync: Update all shock types with same criteria
        if (field === 'subsetName' || field === 'factorShift' || field === 'offsetShift') {
          const changedShock = updatedShocks[shockIndex]
          
          // Also update screenings in real-time when subsetName changes (use original subset name)
          if (field === 'subsetName' && prevDetails.screenings) {
            // Get the original subset name from GET data for this exact shock
            const originalKey = `${scenarioIndex}-${shockIndex}-spreads`
            const originalSubsetName = originalSubsetNamesRef.current.get(originalKey)
            const newSubsetName = String(updatedShocks[shockIndex][field]) 
            
            // Find screening where criteriaName == originalSubsetName, then update it to newSubsetName
            if (originalSubsetName) {
              updatedScreenings = prevDetails.screenings.map((screening: any) => {
                if (screening.criteriaName === originalSubsetName) {
                  return { ...screening, criteriaName: newSubsetName }
                }
                return screening
              })
            }
          }
          
          if (field === 'subsetName') {
            // SUBSET NAME: Sync across ALL scenarios and S/P/V shock types
            const updateShockArrayForName = (shocks: any[]) => 
              shocks.map((shock: any, sIndex: number) => 
                sIndex === shockIndex 
                  ? { ...shock, [field]: newValue }
                  : shock
              )

            updatedScenarios.forEach((scenario: any, sIndex: number) => {
              // Update spreads shocks in other scenarios
              if (sIndex !== scenarioIndex && scenario.spreadsShocks) {
                scenario.spreadsShocks = updateShockArrayForName(scenario.spreadsShocks)
              }
              
              // Update prepayment and volatility shocks in all scenarios
              if (scenario.prepaymentShocks) {
                scenario.prepaymentShocks = updateShockArrayForName(scenario.prepaymentShocks)
              }
              
              if (scenario.volatilityShocks) {
                scenario.volatilityShocks = updateShockArrayForName(scenario.volatilityShocks)
              }
            })
          } else {
            // VALUES (factorShift, offsetShift): Only sync within CURRENT scenario across S/P/V shock types
            const updateShockArrayForValues = (shocks: any[]) => 
              shocks.map((shock: any, sIndex: number) => 
                sIndex === shockIndex 
                  ? { ...shock, [field]: newValue }
                  : shock
              )

            // Only update current scenario's other shock types
            if (updatedScenarios[scenarioIndex].prepaymentShocks) {
              updatedScenarios[scenarioIndex].prepaymentShocks = updateShockArrayForValues(updatedScenarios[scenarioIndex].prepaymentShocks)
            }
            
            if (updatedScenarios[scenarioIndex].volatilityShocks) {
              updatedScenarios[scenarioIndex].volatilityShocks = updateShockArrayForValues(updatedScenarios[scenarioIndex].volatilityShocks)
            }
          }
        }
      }

      updatedScenarios[scenarioIndex] = {
        ...updatedScenarios[scenarioIndex],
        spreadsShocks: updatedShocks,
      }

      return {
        ...prevDetails,
        scenarios: updatedScenarios,
        screenings: updatedScreenings,
      }
    })
  }

  // Handle updates for equity shock values
  const handleEquityShockUpdate = (
    scenarioIndex: number,
    shockIndex: number,
    field: string,
    newValue: number | string,
  ) => {
    // Validate subset name if user is changing subsetName field
    if (field === 'subsetName' && typeof newValue === 'string') {
      const newSubsetName = newValue.trim()
      // Get current subset names excluding the one being edited
      const currentSubsetNames = getCurrentSubsetNames(scenarioIndex, shockIndex, 'equity')
      if (currentSubsetNames.includes(newSubsetName)) {
        setSubsetValidationError(`Subset name "${newSubsetName}" already exists. Please choose a different name.`)
        return 
      }
      setSubsetValidationError('')
    }

    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails) return prevDetails

      const updatedScenarios = [...prevDetails.scenarios]
      const updatedShocks = [...(updatedScenarios[scenarioIndex].equityShocks || [])]
      let updatedScreenings = prevDetails.screenings
      
      if (updatedShocks[shockIndex]) {
        // @ts-ignore - Dynamic field access needed for real-time sync
        const oldValue = updatedShocks[shockIndex][field]
        updatedShocks[shockIndex] = {
          ...updatedShocks[shockIndex],
          [field]: newValue,
        }
        // Real-time sync: Update equity shocks names across scenarios 
        if (field === 'subsetName' || field === 'eqtyMktShock' || field === 'subsetReturnOverride') {
          const changedShock = updatedShocks[shockIndex]
          
          // Also update screenings in real-time when subsetName changes (use original subset name)
          if (field === 'subsetName' && prevDetails.screenings) {
            // Get the original subset name from GET data for this exact shock
            const originalKey = `${scenarioIndex}-${shockIndex}-equity`
            const originalSubsetName = originalSubsetNamesRef.current.get(originalKey)
            const newSubsetName = String(updatedShocks[shockIndex][field]) 
            
            // Find screening where criteriaName == originalSubsetName, then update it to newSubsetName
            if (originalSubsetName) {
              updatedScreenings = prevDetails.screenings.map((screening: any) => {
                if (screening.criteriaName === originalSubsetName) {
                  return { ...screening, criteriaName: newSubsetName }
                }
                return screening
              })
            }
          }
          
          if (field === 'subsetName') {
            // SUBSET NAME: Sync across ALL scenarios within equity type only
            const updateShockArrayForName = (shocks: any[]) => 
              shocks.map((shock: any, sIndex: number) => 
                sIndex === shockIndex 
                ? { ...shock, [field]: newValue }
                : shock
            )

          updatedScenarios.forEach((scenario: any, sIndex: number) => {
              // Update equity shocks in other scenarios only (no cross-shock-type sync)
            if (sIndex !== scenarioIndex && scenario.equityShocks) {
                scenario.equityShocks = updateShockArrayForName(scenario.equityShocks)
            }
          })
          }
        }
      }

      updatedScenarios[scenarioIndex] = {
        ...updatedScenarios[scenarioIndex],
        equityShocks: updatedShocks,
      }

      return {
        ...prevDetails,
        scenarios: updatedScenarios,
        screenings: updatedScreenings,
      }
    })
  }

  // Handle updates for prepayment shock values
  const handlePrepaymentShockUpdate = (
    scenarioIndex: number,
    shockIndex: number,
    field: string,
    newValue: number | string,
  ) => {
    // Validate subset name if user is changing subsetName field
    if (field === 'subsetName' && typeof newValue === 'string') {
      const newSubsetName = newValue.trim()
      // Get current subset names excluding the one being edited
      const currentSubsetNames = getCurrentSubsetNames(scenarioIndex, shockIndex, 'prepayment')
      if (currentSubsetNames.includes(newSubsetName)) {
        setSubsetValidationError(`Subset name "${newSubsetName}" already exists. Please choose a different name.`)
        return 
      }
      setSubsetValidationError('') 
    }

    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails) return prevDetails

      const updatedScenarios = [...prevDetails.scenarios]
      const updatedShocks = [...(updatedScenarios[scenarioIndex].prepaymentShocks || [])]
      let updatedScreenings = prevDetails.screenings
      
      if (updatedShocks[shockIndex]) {
        // @ts-ignore - Dynamic field access needed for real-time sync
        const oldValue = updatedShocks[shockIndex][field]
        updatedShocks[shockIndex] = {
          ...updatedShocks[shockIndex],
          [field]: newValue,
        }
        
        // Real-time sync: Update all shock types with same criteria
        if (field === 'subsetName' || field === 'prepayVal' || field === 'prepayType') {
          // Also update screenings in real-time when subsetName changes (use original subset name)
          if (field === 'subsetName' && prevDetails.screenings) {
            // Get the original subset name from GET data for this exact shock
            const originalKey = `${scenarioIndex}-${shockIndex}-prepayment`
            const originalSubsetName = originalSubsetNamesRef.current.get(originalKey)
            const newSubsetName = String(updatedShocks[shockIndex][field]) 
            
            // Find screening where criteriaName == originalSubsetName, then update it to newSubsetName
            if (originalSubsetName) {
              updatedScreenings = prevDetails.screenings.map((screening: any) => {
                if (screening.criteriaName === originalSubsetName) {
                  return { ...screening, criteriaName: newSubsetName }
                }
                return screening
              })
            }
          }
          
          if (field === 'subsetName') {
            // SUBSET NAME: Sync across ALL scenarios and S/P/V shock types
            const updateShockArrayForName = (shocks: any[]) => 
              shocks.map((shock: any, sIndex: number) => 
                sIndex === shockIndex 
                  ? { ...shock, [field]: newValue }
                  : shock
              )

            updatedScenarios.forEach((scenario: any, sIndex: number) => {
              // Update prepayment shocks in other scenarios
              if (sIndex !== scenarioIndex && scenario.prepaymentShocks) {
                scenario.prepaymentShocks = updateShockArrayForName(scenario.prepaymentShocks)
              }
              
              // Update spreads and volatility shocks in all scenarios
              if (scenario.spreadsShocks) {
                scenario.spreadsShocks = updateShockArrayForName(scenario.spreadsShocks)
              }
              
              if (scenario.volatilityShocks) {
                scenario.volatilityShocks = updateShockArrayForName(scenario.volatilityShocks)
              }
            })
          } else {
            // VALUES (prepayVal, prepayType): Only sync within CURRENT scenario across S/P/V shock types
            const updateShockArrayForValues = (shocks: any[]) => 
              shocks.map((shock: any, sIndex: number) => 
                sIndex === shockIndex 
                  ? { ...shock, [field]: newValue }
                  : shock
              )

            // Only update current scenario's other shock types
            if (updatedScenarios[scenarioIndex].spreadsShocks) {
              updatedScenarios[scenarioIndex].spreadsShocks = updateShockArrayForValues(updatedScenarios[scenarioIndex].spreadsShocks)
            }
            
            if (updatedScenarios[scenarioIndex].volatilityShocks) {
              updatedScenarios[scenarioIndex].volatilityShocks = updateShockArrayForValues(updatedScenarios[scenarioIndex].volatilityShocks)
            }
          }
        }
      }

      updatedScenarios[scenarioIndex] = {
        ...updatedScenarios[scenarioIndex],
        prepaymentShocks: updatedShocks,
      }

      return {
        ...prevDetails,
        scenarios: updatedScenarios,
        screenings: updatedScreenings,
      }
    })
  }

  // Handle updates for volatility shock values
  const handleVolatilityShockUpdate = (
    scenarioIndex: number,
    shockIndex: number,
    field: string,
    newValue: number | string,
  ) => {
    // Validate subset name if user is changing subsetName field
    if (field === 'subsetName' && typeof newValue === 'string') {
      const newSubsetName = newValue.trim()
      // Get current subset names excluding the one being edited
      const currentSubsetNames = getCurrentSubsetNames(scenarioIndex, shockIndex, 'volatility')
      if (currentSubsetNames.includes(newSubsetName)) {
        setSubsetValidationError(`Subset name "${newSubsetName}" already exists. Please choose a different name.`)
        return 
      }
      setSubsetValidationError('') 
    }

    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails) return prevDetails

      const updatedScenarios = [...prevDetails.scenarios]
      const updatedShocks = [...(updatedScenarios[scenarioIndex].volatilityShocks || [])]
      let updatedScreenings = prevDetails.screenings
      
      if (updatedShocks[shockIndex]) {
        // @ts-ignore - Dynamic field access needed for real-time sync
        const oldValue = updatedShocks[shockIndex][field]
        updatedShocks[shockIndex] = {
          ...updatedShocks[shockIndex],
          [field]: newValue,
        }
        
        // Real-time sync: Handle subset name vs value changes differently
        if (field === 'subsetName' || field === 'defaultMult' || field === 'recoveryMult' || field === 'shortVolMult' || field === 'longVolMult') {
          const changedShock = updatedShocks[shockIndex]
          
          // Also update screenings in real-time when subsetName changes (use original subset name)
          if (field === 'subsetName' && prevDetails.screenings) {
            // Get the original subset name from GET data for this exact shock
            const originalKey = `${scenarioIndex}-${shockIndex}-volatility`
            const originalSubsetName = originalSubsetNamesRef.current.get(originalKey)
            const newSubsetName = String(updatedShocks[shockIndex][field]) 
            
            // Find screening where criteriaName == originalSubsetName, then update it to newSubsetName
            if (originalSubsetName) {
              updatedScreenings = prevDetails.screenings.map((screening: any) => {
                if (screening.criteriaName === originalSubsetName) {
                  return { ...screening, criteriaName: newSubsetName }
                }
                return screening
              })
            }
          }
          
          if (field === 'subsetName') {
            // SUBSET NAME: Sync across ALL scenarios and S/P/V shock types
            const updateShockArrayForName = (shocks: any[]) => 
              shocks.map((shock: any, sIndex: number) => 
                sIndex === shockIndex 
                  ? { ...shock, [field]: newValue }
                  : shock
              )

            updatedScenarios.forEach((scenario: any, sIndex: number) => {
              // Update volatility shocks in other scenarios
              if (sIndex !== scenarioIndex && scenario.volatilityShocks) {
                scenario.volatilityShocks = updateShockArrayForName(scenario.volatilityShocks)
              }
              
              // Update spreads and prepayment shocks in all scenarios
              if (scenario.spreadsShocks) {
                scenario.spreadsShocks = updateShockArrayForName(scenario.spreadsShocks)
              }
              
              if (scenario.prepaymentShocks) {
                scenario.prepaymentShocks = updateShockArrayForName(scenario.prepaymentShocks)
              }
            })
          } else {
            // VALUES (defaultMult, recoveryMult, etc.): Only sync within CURRENT scenario across S/P/V shock types
            const updateShockArrayForValues = (shocks: any[]) => 
              shocks.map((shock: any, sIndex: number) => 
                sIndex === shockIndex 
                  ? { ...shock, [field]: newValue }
                  : shock
              )

            // Only update current scenario's other shock types
            if (updatedScenarios[scenarioIndex].spreadsShocks) {
              updatedScenarios[scenarioIndex].spreadsShocks = updateShockArrayForValues(updatedScenarios[scenarioIndex].spreadsShocks)
            }
            
            if (updatedScenarios[scenarioIndex].prepaymentShocks) {
              updatedScenarios[scenarioIndex].prepaymentShocks = updateShockArrayForValues(updatedScenarios[scenarioIndex].prepaymentShocks)
            }
          }
        }
      }

      updatedScenarios[scenarioIndex] = {
        ...updatedScenarios[scenarioIndex],
        volatilityShocks: updatedShocks,
      }

      return {
        ...prevDetails,
        scenarios: updatedScenarios,
        screenings: updatedScreenings,
      }
    })
  }

  const handleShockTypeChange = (
    scenarioIndex: number,
    newShockType: YieldCurveShockTypes,
  ) => {
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails) return prevDetails

      const updatedScenarios: TScenario[] = [...(prevDetails.scenarios || [])]

      // Reset all shock values based on the new shock type
      const updatedShocks = updatedScenarios[
        scenarioIndex
      ].yieldCurveShocks.map((shock) => {
        const resetShock = { ...shock }

        if (newShockType === YieldCurveShockTypes.KEY_RATES) {
          // Reset d-values when switching to keyRates
          resetShock.d1 = 0
          resetShock.d2 = 0
          resetShock.d3 = 0
        } else if (newShockType === YieldCurveShockTypes.D_VALUES) {
          // Reset key rates when switching to dValues
          resetShock.k0_2 = 0
          resetShock.k0_5 = 0
          resetShock.k1 = 0
          resetShock.k2 = 0
          resetShock.k3 = 0
          resetShock.k4 = 0
          resetShock.k5 = 0
          resetShock.k7 = 0
          resetShock.k10 = 0
          resetShock.k15 = 0
          resetShock.k20 = 0
          resetShock.k25 = 0
          resetShock.k30 = 0
          resetShock.k40 = 0
        }

        return resetShock
      })

      updatedScenarios[scenarioIndex] = {
        ...updatedScenarios[scenarioIndex],
        yieldCurveShockType: newShockType,
        yieldCurveShocks: updatedShocks,
      } as TScenario

      return {
        ...prevDetails,
        scenarios: updatedScenarios,
      }
    })
  }

  const handleAddShock = (scenarioIndex: number) => {
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails) return prevDetails

      // Get currently used currencies in this scenario
      const currentScenario = prevDetails.scenarios[scenarioIndex]
      const usedCurrencies = currentScenario.yieldCurveShocks.map(
        (shock) => shock.curve,
      )

      // Get first available unused currency
      const availableCurrencies = getAvailableCurrencies(usedCurrencies)
      const defaultCurrency = availableCurrencies[0] || ''

      // Create new shock with the first available currency
      const newShock: TYieldCurveShock = {
        curve: defaultCurrency,
        d1: 0,
        d2: 0,
        d3: 0,
        k0_2: 0,
        k0_5: 0,
        k1: 0,
        k2: 0,
        k3: 0,
        k4: 0,
        k5: 0,
        k7: 0,
        k10: 0,
        k15: 0,
        k20: 0,
        k25: 0,
        k30: 0,
        k40: 0,
      }
      // Create new scenarios array with updated yieldCurveShocks
      const updatedScenarios = prevDetails.scenarios.map((scenario, index) => {
        if (index === scenarioIndex) {
          return {
            ...scenario,
            yieldCurveShocks: [...scenario.yieldCurveShocks, newShock],
          }
        }
        return scenario
      })

      return {
        ...prevDetails,
        scenarios: updatedScenarios,
      }
    })
  }

  // deleting one scenario from the scenario's using clicked scenarios index.
  const handleDeleteScenario = (scenarioIndex: number) => {
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails) return prevDetails

      // Create new array by filtering out the scenario at scenarioIndex
      const updatedScenarios = prevDetails.scenarios.filter(
        (_, index) => index !== scenarioIndex,
      )
      return { ...prevDetails, scenarios: updatedScenarios }
    })
  }

  //duplicating existing scenario with same name
  const handleDuplicateScenario = (scenarioIndex: number) => {
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails) return prevDetails

      const scenarioToDuplicate = prevDetails.scenarios
        ? JSON.parse(JSON.stringify(prevDetails.scenarios[scenarioIndex]))
        : null
      if (!scenarioToDuplicate) return prevDetails
      // Set the shortName to an empty string
      scenarioToDuplicate.shortName = ''
      scenarioToDuplicate.name = `${scenarioToDuplicate.name} - copy`
      return {
        ...prevDetails,
        scenarios: [...(prevDetails.scenarios || []), scenarioToDuplicate],
      }
    })
  }

  // Add new empty FX shock with a default currency USD
  const handleAddFxShock = (scenarioIndex: number) => {
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails.scenarios) return prevDetails
      // Deep cloning the scenarios array
      const updatedScenarios = JSON.parse(JSON.stringify(prevDetails.scenarios))

      // Get currently used currencies in this scenario
      const usedCurrencies = updatedScenarios[scenarioIndex].fxShocks.map(
        (shock: TFxShock) => shock.currency,
      )

      // Get first available unused currency
      const availableCurrencies = getAvailableCurrencies(usedCurrencies)
      const defaultCurrency = availableCurrencies[0] || ''

      // Create new shock with the first available currency
      const newFxShock: TFxShock = {
        currency: defaultCurrency,
        currReturn: 0,
      }

      updatedScenarios[scenarioIndex] = {
        ...updatedScenarios[scenarioIndex],
        fxShocks: [
          ...(updatedScenarios[scenarioIndex].fxShocks || []),
          newFxShock,
        ],
      }

      return { ...prevDetails, scenarios: updatedScenarios }
    })
  }


  // Add new Spreads shock with custom criteria name from modal
  const handleAddSpreadsShock = (scenarioIndex: number, criteriaName: string, criteriaData?: any) => {
    // Only create new row if valid criteria name is provided
    if (!criteriaName || criteriaName.trim() === '' || criteriaName === 'Default Criteria') {
      return
    }
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails.scenarios) return prevDetails
      
      const updatedScenarios = JSON.parse(JSON.stringify(prevDetails.scenarios))

      const uniqueSubsetName = generateUniqueSubsetName(updatedScenarios, prevDetails.screenings)
      
      const newSpreadsShock = {
        subsetName: uniqueSubsetName,
        factorShift: 1,
        offsetShift: 0,
        screenCriteria: criteriaName,
        criteriaData: criteriaData
      }

      // Real-time sync: Create subset in ALL scenarios and shock types
      
      // Update ALL scenarios
      updatedScenarios.forEach((scenario: any, sIndex: number) => {
        // Add to spreads shocks in all scenarios
        updatedScenarios[sIndex] = {
          ...updatedScenarios[sIndex],
          spreadsShocks: [
            ...(updatedScenarios[sIndex].spreadsShocks || []),
            {
              subsetName: uniqueSubsetName,
              factorShift: 1,
              offsetShift: 0,
              screenCriteria: criteriaName,
              criteriaData: criteriaData
            }
          ],
          // Add to prepayment shocks in all scenarios
          prepaymentShocks: [
            ...(updatedScenarios[sIndex].prepaymentShocks || []),
            {
              subsetName: uniqueSubsetName,
              prepayVal: 1,
              prepayType: 0,
              screenCriteria: criteriaName,
              criteriaData: criteriaData
            }
          ],
          // Add to volatility shocks in all scenarios
          volatilityShocks: [
            ...(updatedScenarios[sIndex].volatilityShocks || []),
            {
              subsetName: uniqueSubsetName,
              defaultMult: 1,
              recoveryMult: 1,
              shortVolMult: 1,
              longVolMult: 1,
              screenCriteria: criteriaName,
              criteriaData: criteriaData
            }
          ]
        }
      })

      return { ...prevDetails, scenarios: updatedScenarios }
    })
  }

  // Add new Equity shock with custom criteria name from modal
  const handleAddEquityShock = (scenarioIndex: number, criteriaName: string, criteriaData?: any) => {
    // Only create new row if valid criteria name is provided
    if (!criteriaName || criteriaName.trim() === '' || criteriaName === 'Default Criteria') {
      return
    }
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails.scenarios) return prevDetails
      
      const updatedScenarios = JSON.parse(JSON.stringify(prevDetails.scenarios))

      const uniqueSubsetName = generateUniqueSubsetName(updatedScenarios, prevDetails.screenings)

      // Add to equity shocks in ALL scenarios only (no cross-shock-type sync)
      
      updatedScenarios.forEach((scenario: any, sIndex: number) => {
        // Add to equity shocks in all scenarios
        updatedScenarios[sIndex] = {
          ...updatedScenarios[sIndex],
          equityShocks: [
            ...(updatedScenarios[sIndex].equityShocks || []),
            {
              subsetName: uniqueSubsetName,
              eqtyMktShock: 0,
              subsetReturnOverride: 0,
              screenCriteria: criteriaName,
              criteriaData: criteriaData
            }
          ]
        }
      })

      return { ...prevDetails, scenarios: updatedScenarios }
    })
  }

  // Add new Prepayment shock with custom criteria name from modal
  const handleAddPrepaymentShock = (scenarioIndex: number, criteriaName: string, criteriaData?: any) => {
    // Only create new row if valid criteria name is provided
    if (!criteriaName || criteriaName.trim() === '' || criteriaName === 'Default Criteria') {
      return
    }
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails.scenarios) return prevDetails
      
      const updatedScenarios = JSON.parse(JSON.stringify(prevDetails.scenarios))

      const uniqueSubsetName = generateUniqueSubsetName(updatedScenarios, prevDetails.screenings)

      // Add to S/P/V shock types in ALL scenarios (exclude equity)
      
      updatedScenarios.forEach((scenario: any, sIndex: number) => {
        // Add to spreads shocks in all scenarios
        updatedScenarios[sIndex] = {
          ...updatedScenarios[sIndex],
          spreadsShocks: [
            ...(updatedScenarios[sIndex].spreadsShocks || []),
            {
              subsetName: uniqueSubsetName,
              factorShift: 1,
              offsetShift: 0,
              screenCriteria: criteriaName,
              criteriaData: criteriaData
            }
          ],
          // Add to prepayment shocks in all scenarios
          prepaymentShocks: [
            ...(updatedScenarios[sIndex].prepaymentShocks || []),
            {
              subsetName: uniqueSubsetName,
              prepayVal: 1,
              prepayType: 0,
              screenCriteria: criteriaName,
              criteriaData: criteriaData
            }
          ],
          // Add to volatility shocks in all scenarios
          volatilityShocks: [
            ...(updatedScenarios[sIndex].volatilityShocks || []),
            {
              subsetName: uniqueSubsetName,
              defaultMult: 1,
              recoveryMult: 1,
              shortVolMult: 1,
              longVolMult: 1,
              screenCriteria: criteriaName,
              criteriaData: criteriaData
            }
          ]
        }
      })

      return { ...prevDetails, scenarios: updatedScenarios }
    })
  }

  // Add new Volatility shock with custom criteria name from modal
  const handleAddVolatilityShock = (scenarioIndex: number, criteriaName: string, criteriaData?: any) => {
    // Only create new row if valid criteria name is provided
    if (!criteriaName || criteriaName.trim() === '' || criteriaName === 'Default Criteria') {
      return
    }
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails.scenarios) return prevDetails
      
      const updatedScenarios = JSON.parse(JSON.stringify(prevDetails.scenarios))

      const uniqueSubsetName = generateUniqueSubsetName(updatedScenarios, prevDetails.screenings)

      // Real-time sync: Create subset in ALL scenarios and shock types
      
      // Update ALL scenarios
      updatedScenarios.forEach((scenario: any, sIndex: number) => {
        // Add to spreads shocks in all scenarios
        updatedScenarios[sIndex] = {
          ...updatedScenarios[sIndex],
          spreadsShocks: [
            ...(updatedScenarios[sIndex].spreadsShocks || []),
            {
              subsetName: uniqueSubsetName,
              factorShift: 1,
              offsetShift: 0,
              screenCriteria: criteriaName,
              criteriaData: criteriaData
            }
          ],
          // Add to prepayment shocks in all scenarios
          prepaymentShocks: [
            ...(updatedScenarios[sIndex].prepaymentShocks || []),
            {
              subsetName: uniqueSubsetName,
              prepayVal: 1,
              prepayType: 0,
              screenCriteria: criteriaName,
              criteriaData: criteriaData
            }
          ],
          // Add to volatility shocks in all scenarios
          volatilityShocks: [
            ...(updatedScenarios[sIndex].volatilityShocks || []),
            {
              subsetName: uniqueSubsetName,
              defaultMult: 1,
              recoveryMult: 1,
              shortVolMult: 1,
              longVolMult: 1,
              screenCriteria: criteriaName,
              criteriaData: criteriaData
            }
          ]
        }
      })

      return { ...prevDetails, scenarios: updatedScenarios }
    })
  }

  // Helper function to generate unique subset name across all scenarios
  const generateUniqueSubsetName = (scenarios: TScenario[], globalScreenings?: any[]): string => {
    const allSubsetNames = new Set<string>()
    
    // Collect all existing subset names from all scenarios and all shock types
    scenarios.forEach((scenario) => {
      scenario.spreadsShocks?.forEach(shock => allSubsetNames.add(shock.subsetName))
      scenario.equityShocks?.forEach(shock => allSubsetNames.add(shock.subsetName))
      scenario.prepaymentShocks?.forEach(shock => allSubsetNames.add(shock.subsetName))
      scenario.volatilityShocks?.forEach(shock => allSubsetNames.add(shock.subsetName))
    })
    
    // Also collect from global screenings if provided
    if (globalScreenings) {
      globalScreenings.forEach(screening => allSubsetNames.add(screening.criteriaName))
    }
    
    // Find the highest number in existing subset names
    let maxNumber = 0
    allSubsetNames.forEach(name => {
      const match = name.match(/^Subset #(\d+)$/)
      if (match) {
        const number = parseInt(match[1], 10)
        if (number > maxNumber) {
          maxNumber = number
        }
      }
    })
    
    // Keep incrementing until we find a name that doesn't exist
    let nextNumber = maxNumber + 1
    while (allSubsetNames.has(`Subset #${nextNumber}`)) {
      nextNumber++
    }
    
    return `Subset #${nextNumber}`
  }

  // Helper function to remove subset from all shock types
  const removeSubsetFromAllShockTypes = (
    scenarios: TScenario[],
    scenarioIndex: number,
    subsetName: string
  ): TScenario[] => {
    const updatedScenarios = [...scenarios]

    // Remove from ALL scenarios, not just the current one
    updatedScenarios.forEach((scenario) => {
      scenario.spreadsShocks = scenario.spreadsShocks?.filter(s => s.subsetName !== subsetName) || []
      scenario.equityShocks = scenario.equityShocks?.filter(s => s.subsetName !== subsetName) || []
      scenario.prepaymentShocks = scenario.prepaymentShocks?.filter(s => s.subsetName !== subsetName) || []
      scenario.volatilityShocks = scenario.volatilityShocks?.filter(s => s.subsetName !== subsetName) || []
    })

    return updatedScenarios
  }

  // Delete individual row from spreads shock table
  const handleDeleteSpreadsShockRow = (
    scenarioIndex: number,
    shockIndex: number,
  ) => {
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails) return prevDetails
      const updatedScenarios: TScenario[] = JSON.parse(
        JSON.stringify(prevDetails.scenarios),
      )

      // Get the subset name before deleting
      const subsetName = updatedScenarios[scenarioIndex].spreadsShocks[shockIndex]?.subsetName
      
      if (subsetName) {
        // Remove this subset from ALL shock types across ALL scenarios
        const finalScenarios = removeSubsetFromAllShockTypes(updatedScenarios, scenarioIndex, subsetName)
        
        // Also remove from global screenings
        const updatedScreenings = prevDetails.screenings?.filter(
          screening => screening.criteriaName !== subsetName
        ) || []
        
        return {
          ...prevDetails,
          scenarios: finalScenarios,
          screenings: updatedScreenings,
        }
      }

      return prevDetails
    })
  }

  // Delete individual row from equity shock table
  const handleDeleteEquityShockRow = (
    scenarioIndex: number,
    shockIndex: number,
  ) => {
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails) return prevDetails
      const updatedScenarios: TScenario[] = JSON.parse(
        JSON.stringify(prevDetails.scenarios),
      )

      // Get the subset name before deleting
      const subsetName = updatedScenarios[scenarioIndex].equityShocks[shockIndex]?.subsetName
      
      if (subsetName) {
        // Remove this subset from ALL shock types across ALL scenarios
        const finalScenarios = removeSubsetFromAllShockTypes(updatedScenarios, scenarioIndex, subsetName)
        
        // Also remove from global screenings
        const updatedScreenings = prevDetails.screenings?.filter(
          screening => screening.criteriaName !== subsetName
        ) || []
        
        return {
          ...prevDetails,
          scenarios: finalScenarios,
          screenings: updatedScreenings,
        }
      }

      return prevDetails
    })
  }

  // Delete individual row from prepayment shock table
  const handleDeletePrepaymentShockRow = (
    scenarioIndex: number,
    shockIndex: number,
  ) => {
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails) return prevDetails
      const updatedScenarios: TScenario[] = JSON.parse(
        JSON.stringify(prevDetails.scenarios),
      )

      // Get the subset name before deleting
      const subsetName = updatedScenarios[scenarioIndex].prepaymentShocks[shockIndex]?.subsetName
      
      if (subsetName) {
        // Remove this subset from ALL shock types across ALL scenarios
        const finalScenarios = removeSubsetFromAllShockTypes(updatedScenarios, scenarioIndex, subsetName)
        
        // Also remove from global screenings
        const updatedScreenings = prevDetails.screenings?.filter(
          screening => screening.criteriaName !== subsetName
        ) || []
        
        return {
          ...prevDetails,
          scenarios: finalScenarios,
          screenings: updatedScreenings,
        }
      }

      return prevDetails
    })
  }

  // Delete individual row from volatility shock table
  const handleDeleteVolatilityShockRow = (
    scenarioIndex: number,
    shockIndex: number,
  ) => {
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails) return prevDetails
      const updatedScenarios: TScenario[] = JSON.parse(
        JSON.stringify(prevDetails.scenarios),
      )

      // Get the subset name before deleting
      const subsetName = updatedScenarios[scenarioIndex].volatilityShocks[shockIndex]?.subsetName
      
      if (subsetName) {
        // Remove this subset from ALL shock types across ALL scenarios
        const finalScenarios = removeSubsetFromAllShockTypes(updatedScenarios, scenarioIndex, subsetName)
        
        // Also remove from global screenings
        const updatedScreenings = prevDetails.screenings?.filter(
          screening => screening.criteriaName !== subsetName
        ) || []
        
        return {
          ...prevDetails,
          scenarios: finalScenarios,
          screenings: updatedScreenings,
        }
      }

      return prevDetails
    })
  }

  const handleHedgedValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails({
      ...reportTemplateDetails,
      hedgedValue: Math.max(0, +e.target.value),
    } as TReportTemplateDetails)
  }

  // delete individual row from yield curve table.
  const handleDeleteYieldCurveShockRow = (
    scenarioIndex: number,
    shockIndex: number,
  ) => {
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails) return prevDetails
      // type assertion to avoid eslint error
      const updatedScenarios: TScenario[] = JSON.parse(
        JSON.stringify(prevDetails.scenarios),
      )

      updatedScenarios[scenarioIndex].yieldCurveShocks = updatedScenarios[
        scenarioIndex
      ].yieldCurveShocks.filter((_, index) => index !== shockIndex)

      return {
        ...prevDetails,
        scenarios: updatedScenarios,
      }
    })
  }

  // The formatting function only shows display value, not the underlying value used for api call
  const getCalculationMethodLabel = (method: CalculationMethods): string => {
    const labels: Record<CalculationMethods, string> = {
      [CalculationMethods.NO_ARBITRAGE]: 'No Arbitrage',
      [CalculationMethods.AGING]: 'Aging',
      [CalculationMethods.FULL_VALIDATION]: 'Full Valuation',
    }
    return labels[method]
  }

  const getHedgingParamLabel = (param: HedgingParamType): string => {
    const labels: Record<HedgingParamType, string> = {
      [HedgingParamType.UNHEDGED]: 'Unhedged',
      [HedgingParamType.HEDGED]: 'Hedged',
      [HedgingParamType.HEDGED_BY_COMPONENT]: 'Hedged by Component',
    }
    return labels[param]
  }

  // delete individual row from fx shock table
  const handleDeleteFxShockRow = (
    scenarioIndex: number,
    shockIndex: number,
  ) => {
    setHasUnsavedChanges(true)
    dispatch(setHasNewModifiedValues(true))
    setReportTemplateDetails((prevDetails) => {
      if (!prevDetails) return prevDetails
      // type assertion to avoid eslint error
      const updatedScenarios: TScenario[] = JSON.parse(
        JSON.stringify(prevDetails.scenarios),
      )

      updatedScenarios[scenarioIndex].fxShocks = updatedScenarios[
        scenarioIndex
      ].fxShocks.filter((_, index) => index !== shockIndex)

      return {
        ...prevDetails,
        scenarios: updatedScenarios,
      }
    })
  }

  const hasDuplicateScenarioNames = reportTemplateDetails.scenarios.map(
    (scenario, index) => {
      return reportTemplateDetails.scenarios.some(
        (s, i) => s.name === scenario.name && i !== index,
      )
    },
  )
  const hasAnyDuplicateScenarios = hasDuplicateScenarioNames.some(
    (hasDuplicate) => hasDuplicate,
  )

  // Dynamic helper function to get current subset names for validation
  const getCurrentSubsetNames = (excludeScenario?: number, excludeShock?: number, excludeShockType?: string) => {
    const allSubsetNames: string[] = []
    
    // Collect all subset names from all scenarios and shock types (only non-empty names)
    reportTemplateDetails.scenarios.forEach((scenario, scenarioIndex) => {
      scenario.spreadsShocks?.forEach((shock, shockIndex) => {
        // Skip the shock being currently edited
        if (excludeScenario === scenarioIndex && excludeShock === shockIndex && excludeShockType === 'spreads') {
          return
        }
        if (shock.subsetName && shock.subsetName.trim() !== '') {
          allSubsetNames.push(shock.subsetName.trim())
        }
      })
      scenario.equityShocks?.forEach((shock, shockIndex) => {
        // Skip the shock being currently edited
        if (excludeScenario === scenarioIndex && excludeShock === shockIndex && excludeShockType === 'equity') {
          return
        }
        if (shock.subsetName && shock.subsetName.trim() !== '') {
          allSubsetNames.push(shock.subsetName.trim())
        }
      })
      scenario.prepaymentShocks?.forEach((shock, shockIndex) => {
        // Skip the shock being currently edited
        if (excludeScenario === scenarioIndex && excludeShock === shockIndex && excludeShockType === 'prepayment') {
          return
        }
        if (shock.subsetName && shock.subsetName.trim() !== '') {
          allSubsetNames.push(shock.subsetName.trim())
        }
      })
      scenario.volatilityShocks?.forEach((shock, shockIndex) => {
        // Skip the shock being currently edited
        if (excludeScenario === scenarioIndex && excludeShock === shockIndex && excludeShockType === 'volatility') {
          return
        }
        if (shock.subsetName && shock.subsetName.trim() !== '') {
          allSubsetNames.push(shock.subsetName.trim())
        }
      })
    })
    
    // Return unique names for validation
    return [...new Set(allSubsetNames)]
  }
  
  // For now, no duplicate validation error (validation happens on user input)
  const hasAnyDuplicateSubsetNames = false

  return (
    <Paper
      sx={{
        ...DEFAULT_CONTAINER_COL_STYLE,
        padding: 2,
        gap: 1,
        position: 'relative',
      }}
    >
      {isLoading && <LoadingCard />}

      <Box
        display='flex'
        justifyContent='space-between'
        py={2}
        boxSizing='border-box'
        data-cy='scenario-template-container'
      >
        {isUpdateMode ? (
          <TextField
            size='small'
            label='Report Template Name'
            type='text'
            value={reportTemplateDetails.name}
            onChange={handleScenarioTemplateChange}
          />
        ) : (
          <Typography variant='h4'>{reportTemplateDetails.name}</Typography>
        )}
        {/* Buttons will be displayed only when details are editable */}
        {!isViewOnly && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={getTooltipMessage()} arrow placement='top'>
              <span>
                <Button
                  data-cy='template-save-button'
                  color='primary'
                  variant='contained'
                  onClick={() => isPayloadContainsZeroRows()}
                  disabled={isSaveButtonDisabled || hasAnyDuplicateScenarios || !!subsetValidationError}
                  size='small'
                >
                  Save
                </Button>
              </span>
            </Tooltip>
            <Button
              data-cy='template-cancel-button'
              color='primary'
              onClick={handleCancelReportTemplateDetailsChange}
              size='small'
            >
              Cancel
            </Button>
          </Box>
        )}

        {isViewOnly && (
          <Button
            variant='outlined'
            color='primary'
            onClick={() => {
              dispatch(setActionMode(ActionMode.UPDATE))
            }}
            size='small'
          >
            Edit Template
          </Button>
        )}
      </Box>
      <Divider sx={{ mb: 1 }} />
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='flex-start'
        py={4}
        boxSizing='border-box'
      >
        <Box display='grid' gap={8}>
          <Box display='flex' gap={4} flexWrap='wrap'>
            <TextField
              data-cy='horizon-input'
              sx={{ width: '150px' }}
              size='small'
              label='Horizon (no of months)'
              type='number'
              value={reportTemplateDetails.horizon}
              disabled={isViewOnly}
              onChange={handleHorizonChange}
            />
            <FormControl>
              <InputLabel id='calculate-method-input'>
                Calculation Method
              </InputLabel>
              <Select
                data-cy='calculation-method-select'
                sx={{ width: '150px' }}
                disabled={isViewOnly}
                size='small'
                id='calculate-method-input'
                label='Calculation Method'
                value={reportTemplateDetails.calculationMethod}
                onChange={handleCalculationMethodChange}
              >
                {Object.values(CalculationMethods).map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {getCalculationMethodLabel(mode)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <InputLabel id='hedging-param-input'>Hedging Param</InputLabel>
              <Select
                data-cy='hedging-param-select'
                sx={{ width: '200px' }}
                disabled={isViewOnly}
                size='small'
                id='hedging-param-input'
                label='Hedging Param'
                value={reportTemplateDetails.hedgingParam}
                onChange={handleHedgingParamChange}
              >
                {Object.values(HedgingParamType).map((mode) => (
                  <MenuItem
                    key={mode}
                    value={mode}
                    disabled={mode === HedgingParamType.HEDGED_BY_COMPONENT}
                  >
                    {getHedgingParamLabel(mode)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* conditionally show hedged value input field based on hedging param */}
            <TextField
              data-cy='hedged-value-input'
              sx={{
                width: '150px',
              }}
              size='small'
              label='Hedged Value (%)'
              type='number'
              value={reportTemplateDetails.hedgedValue}
              InputProps={{
                readOnly: isViewOnly,
                disabled:
                  reportTemplateDetails.hedgingParam !==
                  HedgingParamType.HEDGED,
              }}
              onChange={handleHedgedValueChange}
            />
          </Box>
          <Box display='flex' gap={4} flexWrap='wrap'>
            <FormControl size='small'>
              <InputLabel id='currency-forecast-input'>
                Currency Forecast
              </InputLabel>
              <Select
                data-cy='currency-forecast-select'
                sx={{ width: '150px' }}
                disabled={isViewOnly}
                size='small'
                id='currency-forecast-input'
                label='Currency Forecast'
                value={reportTemplateDetails.currencyForecast}
                onChange={(e: SelectChangeEvent) => {
                  setHasUnsavedChanges(true)
                  dispatch(setHasNewModifiedValues(true))
                  setReportTemplateDetails({
                    ...reportTemplateDetails,
                    currencyForecast: e.target.value as CurrencyForecastType,
                  })
                }}
              >
                <MenuItem value='level'>Level</MenuItem>
                <MenuItem value='return'>Return</MenuItem>
              </Select>
            </FormControl>
            <FormControl size='small'>
              <InputLabel id='currency-relative-input'>
                Currency Relative To
              </InputLabel>
              <Select
                data-cy='currency-relative-select'
                sx={{ width: '150px', marginRight: 1 }}
                disabled={isViewOnly}
                size='small'
                id='currency-relative-input'
                label='Currency Relative To'
                value={reportTemplateDetails.currencyRelative}
                onChange={(e: SelectChangeEvent) => {
                  setHasUnsavedChanges(true)
                  dispatch(setHasNewModifiedValues(true))
                  setReportTemplateDetails({
                    ...reportTemplateDetails,
                    currencyRelative: e.target.value as CurrencyRelativeType,
                  })
                }}
              >
                <MenuItem value='usd'>USD</MenuItem>
                <MenuItem value='baseCur'>Base Currency</MenuItem>
              </Select>
            </FormControl>
            <YieldCurveTypeOptions
              showLabel={true}
              disabled={isViewOnly}
              value={
                reportTemplateDetails.yieldCurveType || YieldCurveValuesType.PAR
              }
              onChange={handleYieldCurveValueChange}
            />
          </Box>
        </Box>
        {!isViewOnly && (
          <Button
            size='small'
            variant='outlined'
            onClick={addNewScenario}
            startIcon={<Add />}
            //Note: maximum 10 scenarios allowed
            disabled={reportTemplateDetails?.scenarios?.length > 9}
            data-cy='add-scenario-button'
          >
            Add Scenario
          </Button>
        )}
      </Box>
      <Dialog
        fullWidth
        maxWidth='xs'
        open={isSaveTemplate}
        onClose={handleClose}
        aria-labelledby='confirmation-dialog-title'
      >
        <DialogTitle id='confirmation-dialog-title'>Template Incomplete</DialogTitle>
        <Divider />
        <DialogContent>
          Your template needs more data to be saved. Please add either:
          <br />• Non-zero values to your Yield Curve or FX data, OR
          <br />• At least one subset in Spreads, Equity, Prepayment, or Volatility tabs
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={handleConfirm} color='primary'>
            OK
          </Button>
        </DialogActions>
      </Dialog>
      {/* Null check before starting mapping over scenarios array */}
      {reportTemplateDetails.scenarios &&
      reportTemplateDetails.scenarios.length > 0
        ? reportTemplateDetails.scenarios.map((scenario: TScenario, index) => {
            return (
              <ScenarioAccordion
                key={index}
                scenario={scenario}
                scenarioIndex={index}
                isReadonly={isViewOnly}
                defaultExpanded={index === 0}
                totalScenarios={reportTemplateDetails.scenarios.length}
                onAddShock={handleAddShock}
                onYieldCurveShockUpdate={handleYieldCurveShockUpdate}
                onSpreadsShockUpdate={handleSpreadsShockUpdate}
                onEquityShockUpdate={handleEquityShockUpdate}
                onPrepaymentShockUpdate={handlePrepaymentShockUpdate}
                onVolatilityShockUpdate={handleVolatilityShockUpdate}
                onShockTypeChange={handleShockTypeChange}
                onScenarioNameChange={handleScenarioNameChange}
                onDeleteScenario={handleDeleteScenario}
                onDuplicateScenario={handleDuplicateScenario}
                onAddFxShock={handleAddFxShock}
                onAddSpreadsShock={handleAddSpreadsShock}
                onAddEquityShock={handleAddEquityShock}
                onAddPrepaymentShock={handleAddPrepaymentShock}
                onAddVolatilityShock={handleAddVolatilityShock}
                onDeleteYieldCurveShockRow={handleDeleteYieldCurveShockRow}
                onDeleteFxShockRow={handleDeleteFxShockRow}
                onDeleteSpreadsShockRow={handleDeleteSpreadsShockRow}
                onDeleteEquityShockRow={handleDeleteEquityShockRow}
                onDeletePrepaymentShockRow={handleDeletePrepaymentShockRow}
                onDeleteVolatilityShockRow={handleDeleteVolatilityShockRow}
                onBothTypesOff={handleBothTypesOff}
                hasError={hasDuplicateScenarioNames[index]}
              />
            )
          })
        : null}
      

      {/* Duplicate scenario name error message */}
      {hasAnyDuplicateScenarios && (
        <Box 
          sx={{ 
            mt: 2, 
            p: 2, 
            backgroundColor: 'warning.light', 
            color: 'warning.contrastText',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            ⚠️ Duplicate scenario names found. Each scenario must have a unique name to save the template.
          </Typography>
        </Box>
      )}

      {/* Subset validation error message */}
      {subsetValidationError && (
        <Box 
          sx={{ 
            mt: 2, 
            p: 2, 
            backgroundColor: 'error.light', 
            color: 'error.contrastText',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            ⚠️ {subsetValidationError}
          </Typography>
        </Box>
      )}


    </Paper>
  )
}
